import {
  useActivePlaylistDescriptors,
  useEstimatedPlaylistDuration,
  useIsRecommendedPlaylist,
  usePlaylistById,
  usePlaylistTags,
  useUserPlaylistData,
} from "@/store/selectors/playlistSelectors";
import {
  useUserCompletedPlaylistKeys,
  useUserInProgressPlaylistKeys,
} from "@/store/selectors/userProfileSelectors";
import { useMemo } from "react";
import { UserProfileNoResults } from "./UserProfileNoResults";
import { Group, Space, Stack } from "@mantine/core";
import { IUserPlaylistData } from "@/types/UserDataTypes";
import { UserProfileCard } from "./UserProfileCard";
import { Text } from "../ui/Text";
import { Badge } from "../ui/Badge";
import { IconClock, IconStar } from "@tabler/icons-react";
import { AdvizerTag } from "../AdvizerTag";

export function UserProfilePlaylists(props: { showCompletedOnly?: boolean }) {
  const { showCompletedOnly } = props;
  const userPlaylistData = useUserPlaylistData();
  const inProgressPlaylistKeys = useUserInProgressPlaylistKeys();
  const completedPlaylistKeys = useUserCompletedPlaylistKeys();

  const playlistKeysToShow = useMemo(() => {
    return showCompletedOnly ? completedPlaylistKeys : inProgressPlaylistKeys;
  }, [showCompletedOnly, inProgressPlaylistKeys, completedPlaylistKeys]);

  if (!playlistKeysToShow.length) {
    return (
      <UserProfileNoResults
        title={
          showCompletedOnly
            ? "You haven't completed any playlists yet."
            : "You haven't started any playlists yet."
        }
        buttonText={
          showCompletedOnly ? "Explore playlists" : "Explore playlists"
        }
        href={showCompletedOnly ? "/playlists" : "/playlists"}
      />
    );
  }

  return (
    <Stack maw={700}>
      {playlistKeysToShow.map((playlistKey) => (
        <UserProfilePlaylistCard
          key={playlistKey}
          playlistKey={playlistKey}
          userPlaylistDataForSpecificPlaylist={
            userPlaylistData.playlists[playlistKey] || null
          }
        />
      ))}
    </Stack>
  );
}

function UserProfilePlaylistCard(props: {
  playlistKey: string;
  userPlaylistDataForSpecificPlaylist: IUserPlaylistData["playlists"][string];
}) {
  const { playlistKey, userPlaylistDataForSpecificPlaylist } = props;
  const playlist = usePlaylistById(playlistKey);
  const playlistDescriptors = useActivePlaylistDescriptors(playlistKey);
  const estimatedPlaylistDuration = useEstimatedPlaylistDuration(playlistKey);
  const playlistTags = usePlaylistTags(playlistKey);
  const isRecommendedPlaylist = useIsRecommendedPlaylist(playlistKey);

  const understandingResponseBefore =
    userPlaylistDataForSpecificPlaylist?.steps?.[
      "understanding-response-before"
    ]?.value;

  const understandingResponseAfter =
    userPlaylistDataForSpecificPlaylist?.steps?.["understanding-response-after"]
      ?.value;

  const percentChangeInUnderstanding = understandingResponseAfter
    ? ((understandingResponseAfter - understandingResponseBefore) /
        understandingResponseBefore) *
      100
    : null;

  const isPositivePercentChangeInUnderstanding =
    percentChangeInUnderstanding && percentChangeInUnderstanding > 0;

  const isNegativePercentChangeInUnderstanding =
    percentChangeInUnderstanding && percentChangeInUnderstanding <= 0;

  const percentChangeInUnderstandingText =
    isPositivePercentChangeInUnderstanding
      ? `+${percentChangeInUnderstanding.toFixed(0)}%`
      : isNegativePercentChangeInUnderstanding
        ? `${percentChangeInUnderstanding.toFixed(0)}%`
        : `0%`;

  const percentChangeInUnderstandingColor =
    isPositivePercentChangeInUnderstanding
      ? "green"
      : isNegativePercentChangeInUnderstanding
        ? "red"
        : "gray";

  const isInProgress = !understandingResponseAfter;

  const PlalistDescriptors = useMemo(() => {
    return (
      <Group>
        <Text c="text.7" lh={1.3} ta="left" size="sm">
          <span style={{ fontWeight: 600 }}>Topics: </span>
          {playlistDescriptors.map((d) => d.text).join(", ")}
        </Text>
      </Group>
    );
  }, [playlistDescriptors]);

  const PlaylistTags = useMemo(() => {
    return (
      <Group gap={10} align="center" justify={"flex-start"} w="100%">
        {estimatedPlaylistDuration && (
          <Badge
            ariaLabel="Estimated playlist duration"
            color="primary"
            variant="filled"
            leftSection={<IconClock size={12} />}
            style={{ textTransform: "none" }}
          >
            {estimatedPlaylistDuration} min
          </Badge>
        )}

        {isRecommendedPlaylist && (
          <>
            <Badge
              ariaLabel="Recommended playlist"
              color="primary"
              size="md"
              leftSection={<IconStar size={12} />}
              style={{ textTransform: "none" }}
            >
              Recommended
            </Badge>
          </>
        )}
        {playlistTags.map((tag) => (
          <AdvizerTag key={tag.id} tag={tag} disableClick={true} maw={250} />
        ))}
      </Group>
    );
  }, [estimatedPlaylistDuration, isRecommendedPlaylist, playlistTags]);

  if (!playlist) return null;

  return (
    <UserProfileCard
      href={`/playlists/${playlistKey}`}
      badge={{
        color: isInProgress ? "primary" : percentChangeInUnderstandingColor,
        label: isInProgress
          ? "In Progress"
          : `${percentChangeInUnderstandingText} understanding`,
      }}
    >
      <Text titleStyle="h4">{playlist.title}</Text>
      <Space h="xs" />
      {PlaylistTags}
      <Space h="xs" />
      {PlalistDescriptors}
      <Space h="md" />
    </UserProfileCard>
  );
}
